// src/components/reviews/ReviewCard.tsx
import React, { useState, useEffect } from 'react';
import { RatingStars } from './RatingStars';
import { HandThumbUpIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { HandThumbUpIcon as HandThumbUpSolidIcon } from '@heroicons/react/24/solid';
import toast from 'react-hot-toast';
import {ProductReview} from "../types";
import {useAuth} from "../contexts/AuthContext";
import {reviewService} from "../services/reviewService";

interface ReviewCardProps {
    review: ProductReview;
    onHelpfulClick?: () => void;
    onEdit?: (review: ProductReview) => void;
    onDelete?: () => void;
}

export const ReviewCard: React.FC<ReviewCardProps> = ({ review, onHelpfulClick, onEdit, onDelete }) => {
    const { user } = useAuth();
    const [isHelpful, setIsHelpful] = useState(false);
    const [helpfulCount, setHelpfulCount] = useState(review.helpful_count);
    const [loading, setLoading] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const isOwner = user?.id === review.customer_id;

    useEffect(() => {
        if (user) {
            checkIfHelpful();
        }
    }, [user, review.id]);

    const checkIfHelpful = async () => {
        if (!user) return;
        const helpful = await reviewService.isReviewHelpfulByUser(review.id, user.id);
        setIsHelpful(helpful);
    };

    const handleHelpfulClick = async () => {
        if (!user) {
            toast.error('Войдите в аккаунт, чтобы отметить отзыв');
            return;
        }

        if (isHelpful) return;

        setLoading(true);
        try {
            await reviewService.markHelpful(review.id, user.id);
            setIsHelpful(true);
            setHelpfulCount(prev => prev + 1);
            onHelpfulClick?.();
            toast.success('Спасибо за оценку!');
        } catch (error) {
            console.error('Error marking helpful:', error);
            toast.error('Ошибка');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!isOwner) return;

        const confirmed = window.confirm('Вы уверены, что хотите удалить этот отзыв?');
        if (!confirmed) return;

        setIsDeleting(true);
        try {
            await reviewService.deleteReview(review.id);
            toast.success('Отзыв удален');
            onDelete?.();
        } catch (error: any) {
            console.error('Error deleting review:', error);
            toast.error(error.message || 'Ошибка при удалении отзыва');
        } finally {
            setIsDeleting(false);
        }
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('ru-RU', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    return (
        <div className="review-card" style={{
            borderBottom: '1px solid #e5e7eb',
            padding: '12px',
            transition: 'all 0.3s ease'
        }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px', flexWrap: 'wrap' }}>
                        <strong className="user-name" >
                            {review.customer?.first_name} {review.customer?.last_name?.[0]}.
                        </strong>
                        <RatingStars rating={review.rating} size={16} />
                    </div>
                    <span style={{ fontSize: '12px', color: '#9ca3af' }}>{formatDate(review.created_at)}</span>
                    {review.updated_at !== review.created_at && (
                        <span style={{ fontSize: '11px', color: '#9ca3af', marginLeft: '8px' }}>
                            (отредактировано)
                        </span>
                    )}
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    {review.is_verified_purchase && (
                        <span style={{
                            fontSize: '12px',
                            color: '#059669',
                            backgroundColor: '#d1fae5',
                            padding: '4px 8px',
                            borderRadius: '12px'
                        }}>
                            Подтверждённая покупка
                        </span>
                    )}
                    {isOwner && (
                        <div style={{ display: 'flex', gap: '4px' }}>
                            <button
                                onClick={() => onEdit?.(review)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '4px',
                                    borderRadius: '4px',
                                    color: '#6b7280'
                                }}
                                title="Редактировать"
                            >
                                <PencilIcon style={{ width: 16, height: 16 }} />
                            </button>
                            <button
                                onClick={handleDelete}
                                disabled={isDeleting}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: isDeleting ? 'default' : 'pointer',
                                    padding: '4px',
                                    borderRadius: '4px',
                                    color: '#ef4444'
                                }}
                                title="Удалить"
                            >
                                <TrashIcon style={{ width: 16, height: 16 }} />
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {review.title && (
                <h4 style={{ fontSize: '16px', fontWeight: 600, margin: '12px 0 8px 0', color: '#111827' }}>
                    {review.title}
                </h4>
            )}

            <p style={{ fontSize: '14px', lineHeight: '1.6', color: '#4b5563', marginBottom: '12px' }}>
                {review.comment}
            </p>

            {(review.advantages || review.disadvantages) && (
                <div style={{
                    backgroundColor: '#f9fafb',
                    padding: '12px',
                    borderRadius: '8px',
                    marginBottom: '12px',
                    fontSize: '14px'
                }}>
                    {review.advantages && (
                        <div style={{ marginBottom: '8px' }}>
                            <strong style={{ color: '#059669' }}>Достоинства:</strong>
                            <span style={{ color: '#4b5563', marginLeft: '8px' }}>{review.advantages}</span>
                        </div>
                    )}
                    {review.disadvantages && (
                        <div>
                            <strong style={{ color: '#dc2626' }}>Недостатки:</strong>
                            <span style={{ color: '#4b5563', marginLeft: '8px' }}>{review.disadvantages}</span>
                        </div>
                    )}
                </div>
            )}

            <button
                onClick={handleHelpfulClick}
                disabled={loading || isHelpful}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: 'none',
                    border: 'none',
                    cursor: isHelpful ? 'default' : 'pointer',
                    color: isHelpful ? '#059669' : '#6b7280',
                    fontSize: '13px',
                    padding: '6px 12px',
                    borderRadius: '20px',
                    transition: 'all 0.2s ease',
                    backgroundColor: isHelpful ? '#ecfdf5' : 'transparent'
                }}
            >
                {isHelpful ? <HandThumbUpSolidIcon style={{ width: 16, height: 16 }} /> : <HandThumbUpIcon style={{ width: 16, height: 16 }} />}
                <span>Полезно ({helpfulCount})</span>
            </button>
        </div>
    );
};